import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, Plus, Scale, Trash2 } from 'lucide-react'
import { useFieldArray, useForm } from 'react-hook-form'
import { PageShell } from '@/components/common/PageShell'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { DateInput } from '@/components/ui/DateInput'
import {
  useCreateTaxRule,
  useTaxRule,
  useUpdateTaxRule,
} from '@/features/tax-rules/hooks/useTaxRules'
import type {
  CreateTaxRuleDto,
  IncomeBasis,
  SalaryComponent,
  TaxBracket,
} from '@/features/tax-rules/types/tax-rule.types'
import {
  getTaxRuleFieldErrors,
  isConflictError,
} from '@/features/tax-rules/utils/taxRuleFieldErrors'
import { SupersededNotice } from './TaxRuleSuperseded'
import { isSuperseded, latestVersionPath } from '@/features/tax-rules/utils/taxRuleVersions'

const INCOME_BASIS_OPTIONS = [
  { value: 'annual', label: 'Annual (slabs are yearly amounts)' },
  { value: 'monthly', label: 'Monthly (slabs are per-month amounts)' },
  { value: 'per_period', label: 'Per pay period' },
]

const TAX_BASE_OPTIONS: Array<{ value: SalaryComponent; label: string }> = [
  { value: 'basic_salary', label: 'Basic salary' },
  { value: 'gross_salary', label: 'Gross salary (all taxable pay)' },
  { value: 'bonus', label: 'Bonus only' },
  { value: 'allowance', label: 'Allowance only' },
  { value: 'overtime', label: 'Overtime only' },
  // No "Commission only" until commission entries feed payroll (card 3.11):
  // payroll refuses to run while any rule taxes commission.
]

/** Form fields that can show an API error inline; anything else is listed above the buttons. */
const INLINE_FIELD =
  /^(name|country|appliesTo|incomeBasis|effectiveFrom|effectiveTo|brackets\.\d+\.(min|max|rate|fixedComponent))$/

interface FormBracket {
  min: number
  max: number | ''
  rate: number
  /** Fixed tax amount added on top of the percentage for this slab (0 = none). */
  fixedComponent: number
}

interface FormValues {
  name: string
  country: string
  appliesTo: SalaryComponent[]
  incomeBasis: IncomeBasis
  brackets: FormBracket[]
  effectiveFrom: string
  effectiveTo: string
  isActive: boolean
}

const inputCls =
  'h-8 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
const labelCls = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'
const helpCls = 'mt-1 text-xs text-gray-500 dark:text-gray-400'
const errorCls = 'mt-1 text-xs text-red-600 dark:text-red-400'

export function TaxRuleFormPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = !!id

  const { data: rule, isLoading, refetch } = useTaxRule(id ?? null)
  const createMut = useCreateTaxRule()
  const updateMut = useUpdateTaxRule()
  const [otherErrors, setOtherErrors] = useState<string[]>([])
  const [conflict, setConflict] = useState(false)

  const form = useForm<FormValues>({
    defaultValues: {
      name: '',
      country: '',
      appliesTo: ['basic_salary'],
      incomeBasis: 'monthly',
      brackets: [{ min: 0, max: '', rate: 0, fixedComponent: 0 }],
      effectiveFrom: '',
      effectiveTo: '',
      isActive: true,
    },
  })

  const bracketsField = useFieldArray({
    control: form.control,
    name: 'brackets',
  })
  const watchedAppliesTo = form.watch('appliesTo')
  const watchedIncomeBasis = form.watch('incomeBasis')

  useEffect(() => {
    if (!rule) return
    form.reset({
      name: rule.name,
      country: rule.country,
      appliesTo: rule.appliesTo.length > 0 ? rule.appliesTo : ['basic_salary'],
      incomeBasis: rule.incomeBasis ?? 'monthly',
      brackets:
        rule.brackets.length > 0
          ? rule.brackets.map((b) => ({
              min: b.min,
              max: b.max ?? '',
              rate: b.rate,
              fixedComponent: b.fixedComponent ?? 0,
            }))
          : [{ min: 0, max: '', rate: 0, fixedComponent: 0 }],
      effectiveFrom: rule.effectiveFrom ? rule.effectiveFrom.slice(0, 10) : '',
      effectiveTo: rule.effectiveTo ? rule.effectiveTo.slice(0, 10) : '',
      isActive: rule.isActive,
    })
  }, [rule, form])

  if (isEdit && isLoading) {
    return (
      <PageShell
        breadcrumbItems={[{ label: 'Tax Rules', to: '/tax-rules' }, { label: 'Edit' }]}
        icon={Scale}
        title="Edit Tax Rule"
        className="gap-0"
        hideHeader
      >
        <TableSkeleton rows={4} cols={2} />
      </PageShell>
    )
  }

  const isLegacyRule = !!rule && rule.ruleType !== 'salary_range'

  const showServerErrors = (error: unknown): void => {
    if (isConflictError(error)) {
      // Someone saved a newer version first; reload to learn which one.
      setConflict(true)
      void refetch()
      return
    }
    const rest: string[] = []
    for (const { field, message } of getTaxRuleFieldErrors(error)) {
      if (INLINE_FIELD.test(field)) {
        form.setError(field as keyof FormValues, { type: 'server', message })
      } else {
        rest.push(message)
      }
    }
    setOtherErrors(rest)
  }

  const handleSubmit = (values: FormValues): void => {
    form.clearErrors()
    setOtherErrors([])
    setConflict(false)
    const body: CreateTaxRuleDto = {
      name: values.name.trim(),
      country: values.country.toUpperCase(),
      ruleType: 'salary_range',
      appliesTo: values.appliesTo,
      incomeBasis: values.incomeBasis,
      effectiveFrom: values.effectiveFrom,
      effectiveTo: values.effectiveTo || undefined,
      isActive: values.isActive,
      brackets: values.brackets.map<TaxBracket>((b) => ({
        min: Number(b.min),
        max: b.max === '' || b.max === undefined ? null : Number(b.max),
        rate: Number(b.rate) || 0,
        fixedComponent: Number(b.fixedComponent) || 0,
      })),
    }

    if (isEdit && id) {
      updateMut.mutate(
        { id, body },
        { onSuccess: () => navigate('/tax-rules'), onError: showServerErrors }
      )
    } else {
      createMut.mutate(body, {
        onSuccess: () => navigate('/tax-rules'),
        onError: showServerErrors,
      })
    }
  }

  const saving = createMut.isPending || updateMut.isPending
  const { errors } = form.formState

  const toggleAppliesTo = (component: SalaryComponent): void => {
    const current = watchedAppliesTo
    if (current.includes(component)) {
      if (current.length === 1) return
      form.setValue(
        'appliesTo',
        current.filter((value) => value !== component)
      )
      return
    }

    if (component === 'gross_salary') {
      form.setValue('appliesTo', ['gross_salary'])
      return
    }

    form.setValue('appliesTo', [...current.filter((value) => value !== 'gross_salary'), component])
  }

  if (rule && isSuperseded(rule) && !conflict) {
    return (
      <PageShell
        breadcrumbItems={[{ label: 'Tax Rules', to: '/tax-rules' }, { label: 'Edit' }]}
        icon={Scale}
        title={`Edit Tax Rule — ${rule.name}`}
        className="gap-0"
        hideHeader
      >
        <div className="max-w-2xl">
          <SupersededNotice rule={rule} />
        </div>
      </PageShell>
    )
  }

  if (isLegacyRule) {
    return (
      <PageShell
        breadcrumbItems={[{ label: 'Tax Rules', to: '/tax-rules' }, { label: 'Edit' }]}
        icon={Scale}
        title={`Edit Tax Rule — ${rule.name}`}
        className="gap-0"
        hideHeader
      >
        <div className="max-w-2xl rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold">
                This older tax rule can’t be edited in the simplified form.
              </p>
              <p className="mt-1">
                The admin screen now supports salary slabs only. This rule uses
                <span className="mx-1 font-medium">{rule.ruleType.replace('_', ' ')}</span>
                and is kept for compatibility.
              </p>
              <div className="mt-4 flex gap-3">
                <Button variant="secondary" onClick={() => navigate('/tax-rules')}>
                  Back to Tax Rules
                </Button>
                <Button onClick={() => navigate('/tax-rules/new')}>
                  Create New Salary Slab Rule
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Tax Rules', to: '/tax-rules' },
        { label: isEdit ? `Edit v${rule?.version ?? ''}` : 'New' },
      ]}
      icon={Scale}
      title={isEdit ? `Edit Tax Rule — ${rule?.name ?? ''}` : 'New Tax Rule'}
      description={
        isEdit
          ? `Saving will create version ${(rule?.version ?? 0) + 1}`
          : 'Create salary slabs for payroll tax. New rules default to basic salary.'
      }
      className="gap-0"
      hideHeader
    >
      <form className="max-w-3xl space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-100">
          <div className="font-semibold">Salary slabs</div>
          <p className="mt-1">
            This form creates a salary-slab tax rule. Choose what income it should apply to, then
            define the slabs. Each slab can use a tax percentage, a fixed amount, or both.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelCls}>Rule Name</label>
            <input
              className={inputCls}
              {...form.register('name', {
                validate: (v) => v.trim().length > 0 || 'Rule name is required',
              })}
            />
            {errors.name?.message && <p className={errorCls}>{errors.name.message}</p>}
          </div>
          <div>
            <label className={labelCls}>Country</label>
            <input
              className={`${inputCls} uppercase`}
              maxLength={2}
              placeholder="PK"
              {...form.register('country', {
                required: 'Country is required',
                minLength: { value: 2, message: 'Use the two-letter country code, e.g. PK' },
              })}
            />
            {errors.country?.message && <p className={errorCls}>{errors.country.message}</p>}
          </div>
        </div>

        <div>
          <label className={labelCls}>Apply Tax On</label>
          <div className="flex flex-wrap gap-2">
            {TAX_BASE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => toggleAppliesTo(option.value)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  watchedAppliesTo.includes(option.value)
                    ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className={helpCls}>
            Select one or more income types. `Gross salary` replaces the individual items because it
            already includes everything taxable.
          </p>
          {errors.appliesTo?.message && <p className={errorCls}>{errors.appliesTo.message}</p>}
        </div>

        <div>
          <Select
            label="Salary Amounts Are Based On"
            options={INCOME_BASIS_OPTIONS}
            value={watchedIncomeBasis}
            onChange={(v) => form.setValue('incomeBasis', v as IncomeBasis)}
          />
          <p className={helpCls}>
            Choose whether these slab amounts are monthly, yearly, or per payroll period.
          </p>
          {errors.incomeBasis?.message && <p className={errorCls}>{errors.incomeBasis.message}</p>}
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className={labelCls}>Salary Slabs</label>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => bracketsField.append({ min: 0, max: '', rate: 0, fixedComponent: 0 })}
            >
              <Plus className="mr-1 h-3 w-3" />
              Add Slab
            </Button>
          </div>
          <p className={`${helpCls} mb-3`}>
            Add salary ranges and, for each range, a tax percentage and/or a fixed amount. Use the
            percentage alone, the fixed amount alone, or both (the fixed amount is added on top of
            the percentage). Leave the final “To salary” empty for an open-ended top slab.
          </p>
          <div className="mb-2 hidden grid-cols-[1fr_1fr_0.7fr_0.9fr_auto] gap-2 px-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 md:grid">
            <span>From Salary</span>
            <span>To Salary</span>
            <span>Tax %</span>
            <span>Fixed Amount</span>
            <span />
          </div>
          {bracketsField.fields.map((field, i) => {
            const rowErrors = (['min', 'max', 'rate', 'fixedComponent'] as const)
              .map((key) => errors.brackets?.[i]?.[key]?.message)
              .filter((message): message is string => !!message)
            return (
              <div key={field.id} data-bracket-row={i} className="mb-3">
                <div className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 p-3 md:grid-cols-[1fr_1fr_0.7fr_0.9fr_auto] md:items-center md:border-0 md:p-0 dark:border-gray-700">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="From salary"
                    className={inputCls}
                    {...form.register(`brackets.${i}.min`, { valueAsNumber: true })}
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="To salary"
                    className={inputCls}
                    {...form.register(`brackets.${i}.max`, {
                      setValueAs: (v) => (v === '' || v == null ? '' : Number(v)),
                    })}
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="Tax %"
                    className={inputCls}
                    {...form.register(`brackets.${i}.rate`, { valueAsNumber: true })}
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Fixed amount"
                    className={inputCls}
                    {...form.register(`brackets.${i}.fixedComponent`, {
                      setValueAs: (v) => (v === '' || v == null ? 0 : Number(v)),
                    })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="justify-self-start md:justify-self-center"
                    onClick={() => {
                      if (bracketsField.fields.length === 1) {
                        form.setValue('brackets.0', { min: 0, max: '', rate: 0, fixedComponent: 0 })
                        return
                      }
                      bracketsField.remove(i)
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </Button>
                </div>
                {rowErrors.map((message) => (
                  <p key={message} className={errorCls}>
                    {message}
                  </p>
                ))}
              </div>
            )
          })}
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
          Tax will be applied on{' '}
          <span className="font-semibold">
            {watchedAppliesTo
              .map(
                (value) => TAX_BASE_OPTIONS.find((option) => option.value === value)?.label ?? value
              )
              .join(', ')}
          </span>
          .
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className={labelCls}>Effective From</label>
            <DateInput
              error={errors.effectiveFrom?.message}
              {...form.register('effectiveFrom', { required: 'Effective from date is required' })}
            />
          </div>
          <div>
            <label className={labelCls}>Effective To (optional)</label>
            <DateInput error={errors.effectiveTo?.message} {...form.register('effectiveTo')} />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input type="checkbox" {...form.register('isActive')} />
          Active
        </label>

        {conflict && (
          <div
            role="alert"
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200"
          >
            This rule was updated by someone else.{' '}
            <Link
              className="font-medium underline"
              to={rule ? latestVersionPath(rule) : '/tax-rules'}
            >
              Open the latest version
            </Link>
          </div>
        )}

        {otherErrors.length > 0 && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200"
          >
            <p className="font-semibold">This rule can’t be saved yet:</p>
            <ul className="mt-1 list-disc pl-5">
              {otherErrors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <Button variant="secondary" type="button" onClick={() => navigate('/tax-rules')}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={saving}>
            {isEdit ? 'Save (New Version)' : 'Create Rule'}
          </Button>
        </div>
      </form>
    </PageShell>
  )
}

TaxRuleFormPage.displayName = 'TaxRuleFormPage'
